import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import FullBlockingModal from '../../common/FullBlockingModal';
import { Button } from '../../common/Button';
import { useAdminStore } from '../../../stores/adminStore';
import { useCityStore } from '../../../stores/cityStore';

interface Props {
  onClose: () => void;
}

/** Empty input → null (omit). Non-empty invalid → invalid. */
function parseOptionalCoord(raw: string): { value: number | null; invalid: boolean } {
  const trimmed = raw.trim();
  if (trimmed === '') return { value: null, invalid: false };
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return { value: null, invalid: true };
  return { value: n, invalid: false };
}

const AddCityModal: React.FC<Props> = ({ onClose }) => {
  const { t } = useTranslation(['common', 'admin']);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [saving, setSaving] = useState(false);

  const { insertCity, fetchCities } = useAdminStore();
  const fetchAllCitiesForDropdown = useCityStore(s => s.fetchCities);

  const latResult = parseOptionalCoord(latitude);
  const lngResult = parseOptionalCoord(longitude);

  const handleSubmit = async () => {
    if (latResult.invalid || lngResult.invalid) return;

    setSaving(true);
    try {
      await insertCity({
        name: name.trim(),
        code: code.trim(),
        latitude: latResult.value,
        longitude: lngResult.value,
      });
      await fetchCities({
        offsetCount: 0,
        search: useAdminStore.getState().citiesSearch,
      });
      await fetchAllCitiesForDropdown();
      onClose();
    } catch (err) {
      console.error('Failed to insert city', err);
      toast.error(t('generic_error', { ns: 'common' }));
    } finally {
      setSaving(false);
    }
  };

  const canSubmit =
    name.trim() !== '' && code.trim() !== '' && !latResult.invalid && !lngResult.invalid;

  return (
    <FullBlockingModal innerClassName="md:max-w-[36rem]">
      <h2 className="text-2xl font-bold text-secondary mb-4">
        {t('add_new_city', { ns: 'common' })}
      </h2>

      <p className="text-sm text-gray-600 mb-4">{t('city_code_hint', { ns: 'admin' })}</p>

      <div className="flex flex-col gap-3">
        <label className="text-sm font-semibold text-gray-700">
          {t('table_fields.name', { ns: 'admin' })}
        </label>
        <input
          value={name}
          onChange={e => setName(e.target.value)}
          className="border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
        />

        <label className="text-sm font-semibold text-gray-700">
          {t('table_fields.code', { ns: 'admin' })}
        </label>
        <input
          value={code}
          onChange={e => setCode(e.target.value)}
          className="border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="10"
        />

        <label className="text-sm font-semibold text-gray-700">
          {t('table_fields.latitude', { ns: 'admin' })}
        </label>
        <input
          value={latitude}
          onChange={e => setLatitude(e.target.value)}
          className="border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="45.5017"
        />
        {latResult.invalid && (
          <p className="text-sm text-red-600">{t('invalid_number', { ns: 'admin' })}</p>
        )}

        <label className="text-sm font-semibold text-gray-700">
          {t('table_fields.longitude', { ns: 'admin' })}
        </label>
        <input
          value={longitude}
          onChange={e => setLongitude(e.target.value)}
          className="border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="-73.5673"
        />
        {lngResult.invalid && (
          <p className="text-sm text-red-600">{t('invalid_number', { ns: 'admin' })}</p>
        )}
      </div>

      <div className="flex flex-row md:flex-row justify-end gap-4 mt-6">
        <Button
          variant="simple-text"
          onClick={onClose}
          className="font-bold rounded-full px-8 py-2 w-full md:w-auto"
          type="button"
        >
          {t('common:form_actions.cancel', { ns: 'common' })}
        </Button>

        <Button
          variant="primary"
          onClick={handleSubmit}
          className="font-bold rounded-full px-8 py-2 w-full md:w-auto"
          type="button"
          disabled={saving || !canSubmit}
        >
          {saving
            ? t('loading', { ns: 'common' })
            : t('common:form_actions.save', { ns: 'common' })}
        </Button>
      </div>
    </FullBlockingModal>
  );
};

export default AddCityModal;
